/*
 * PkiUI — shared rendering for the certificate tools.
 *
 * The engine (public/js/pki/*) is language-free and emits codes; this layer
 * turns codes into the viewer's language and into DOM. Keeping it in one file
 * means the seven tools cannot drift apart visually, and a fix to the findings
 * list lands on all of them at once.
 *
 * All copy comes from window.__pkiI18n, injected by the blade from the nine
 * locale files. Nothing here contains a user-visible English string.
 */
(function () {
    'use strict';

    var I18N = {};

    function init(dict) { I18N = dict || {}; }

    /* ── i18n ──────────────────────────────────────────────────── */

    function t(key, params) {
        var parts = String(key).split('.'), cur = I18N;
        for (var i = 0; i < parts.length; i++) {
            if (cur == null) break;
            cur = cur[parts[i]];
        }
        if (cur == null) return '';
        return substitute(String(cur), params);
    }

    function substitute(text, params) {
        if (!params) return text;
        return text.replace(/:([a-zA-Z_]+)/g, function (m, name) {
            return params[name] !== undefined && params[name] !== null ? String(params[name]) : m;
        });
    }

    /* ── escaping ──────────────────────────────────────────────── */

    // Certificate contents are arbitrary user input — a subject can legally
    // contain "<script>". Everything that reaches innerHTML goes through here.
    function esc(s) {
        return String(s == null ? '' : s)
            .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    }

    function code(s) { return '<code>' + esc(s) + '</code>'; }

    /* ── formatting ────────────────────────────────────────────── */

    /**
     * Dates render in the VIEWER's timezone, not UTC. A certificate expiring
     * "at 23:00" means nothing until you know whose clock that is, and a page
     * that silently prints UTC makes people miscount the last day.
     */
    function date(d) {
        if (!d) return '—';
        var dt = d instanceof Date ? d : new Date(d);
        if (isNaN(dt.getTime())) return '—';
        try {
            return dt.toLocaleString(window.__LOCALE || undefined, {
                year: 'numeric', month: 'short', day: 'numeric',
                hour: '2-digit', minute: '2-digit'
            });
        } catch (e) { return dt.toISOString().replace('T', ' ').slice(0, 16); }
    }

    function bytesLabel(n) {
        if (n < 1024) return n + ' B';
        if (n < 1024 * 1024) return (n / 1024).toFixed(1) + ' KB';
        return (n / 1048576).toFixed(2) + ' MB';
    }

    /* ── verdict banner ────────────────────────────────────────── */

    var VERDICT_ICON = { ok: '✅', warning: '⚠️', error: '⛔' };

    function verdict(level, titleKey, subtitle) {
        return '<div class="pki-verdict ' + esc(level) + '">' +
            '<span class="pki-verdict-icon" aria-hidden="true">' + VERDICT_ICON[level] + '</span>' +
            '<div><div class="pki-verdict-text">' + esc(t(titleKey) || titleKey) + '</div>' +
            (subtitle ? '<div class="pki-verdict-sub">' + subtitle + '</div>' : '') +
            '</div></div>';
    }

    /* ── findings ──────────────────────────────────────────────── */

    var SEV_MARK = { error: '!', warning: '!', info: 'i', good: '✓' };

    /**
     * Render ranked findings. Positives are collapsed behind a toggle so a
     * healthy certificate reads as "nothing wrong" instead of a green wall.
     */
    function findings(list, opts) {
        opts = opts || {};
        var Analyze = window.PKI && window.PKI.Analyze;
        var ranked = Analyze ? Analyze.rank(list) : list.slice();
        var problems = ranked.filter(function (f) { return f.severity !== 'good'; });
        var goods = ranked.filter(function (f) { return f.severity === 'good'; });

        var html = '';
        if (problems.length) {
            html += '<div class="pki-findings">' + problems.map(row).join('') + '</div>';
        }
        if (goods.length) {
            var id = 'pkiGoods' + Math.random().toString(36).slice(2, 8);
            html += '<div class="pki-findings" style="margin-top:10px">' +
                '<button type="button" class="pki-goods-toggle" data-goods-toggle="' + id + '" aria-expanded="false">' +
                '▸ ' + esc(substitute(t('ui.checks_passed'), { count: goods.length })) + '</button>' +
                '<div class="pki-goods" id="' + id + '" hidden>' + goods.map(row).join('') + '</div>' +
                '</div>';
        }
        return html;

        function row(f) {
            var base = 'findings.' + f.code;
            var title = t(base + '.t', f.params) || f.code;
            var desc = t(base + '.d', f.params);
            var fix = t(base + '.f', f.params);
            return '<div class="pki-finding ' + esc(f.severity) + '">' +
                '<span class="pki-finding-dot" aria-hidden="true">' + (SEV_MARK[f.severity] || '') + '</span>' +
                '<div class="pki-finding-body">' +
                '<div class="pki-finding-title">' + esc(title) + '</div>' +
                (desc ? '<div class="pki-finding-desc">' + esc(desc) + '</div>' : '') +
                (fix ? '<div class="pki-finding-fix">' + esc(fix) + '</div>' : '') +
                '</div></div>';
        }
    }

    /* ── field tables ──────────────────────────────────────────── */

    function fields(rows) {
        var body = rows.filter(Boolean).map(function (r) {
            var cls = 'v' + (r.plain ? ' plain' : '');
            return '<div class="k">' + esc(r.k) + '</div><div class="' + cls + '">' + (r.html || esc(r.v)) + '</div>';
        }).join('');
        return '<div class="pki-fields">' + body + '</div>';
    }

    function section(title, inner) {
        return '<div class="pki-section"><h3 class="pki-section-title">' + esc(title) + '</h3>' + inner + '</div>';
    }

    function chips(values, cls) {
        if (!values || !values.length) return '<span class="pki-hint">—</span>';
        return '<div class="pki-chips">' + values.map(function (v) {
            return '<span class="pki-chip ' + (cls || '') + '">' + esc(v) + '</span>';
        }).join('') + '</div>';
    }

    /**
     * A distinguished name, rendered attribute by attribute rather than as one
     * opaque string — nobody reads "CN=x,OU=y,O=z,L=…,ST=…,C=…" comfortably,
     * and the pieces are what a CSR form asks for.
     */
    function dn(name) {
        if (!name || !name.attributes.length) return '<span class="pki-hint">' + esc(t('ui.empty_dn')) + '</span>';
        return '<div class="pki-fields">' + name.attributes.map(function (a) {
            return '<div class="k">' + esc(a.short) + ' · ' + esc(a.label) + '</div>' +
                   '<div class="v">' + esc(a.value) + '</div>';
        }).join('') + '</div>';
    }

    /* ── output blocks ─────────────────────────────────────────── */

    /**
     * A copyable/downloadable text block. `secret` blurs the content until the
     * user asks — a private key on screen in an office or on a shared stream is
     * a real hazard, and the blur costs nothing.
     */
    function output(opts) {
        var id = opts.id || ('pkiOut' + Math.random().toString(36).slice(2, 8));
        var tools = '<button type="button" class="pki-btn-sec" data-pki-copy="' + id + '">' +
                esc(t('ui.copy')) + '</button>';
        if (opts.filename) {
            tools += '<button type="button" class="pki-btn-sec" data-pki-download="' + id + '" ' +
                'data-filename="' + esc(opts.filename) + '">' + esc(t('ui.download')) + '</button>';
        }
        if (opts.secret) {
            tools = '<button type="button" class="pki-btn-sec" data-pki-reveal="' + id + '">' +
                esc(t('ui.reveal')) + '</button>' + tools;
        }
        return '<div class="pki-out' + (opts.secret ? ' secret' : '') + '" data-out="' + id + '">' +
            '<div class="pki-out-head">' +
            '<span class="pki-out-title">' + esc(opts.title || '') + '</span>' +
            '<span class="pki-out-tools">' + tools + '</span></div>' +
            '<pre class="pki-out-body' + (opts.wrap ? ' wrap' : '') + '" id="' + id + '">' + esc(opts.text) + '</pre>' +
            '</div>';
    }

    /**
     * One delegated listener for every copy / download / reveal / toggle in the
     * page, so freshly rendered output needs no re-binding.
     */
    function bind(container) {
        container.addEventListener('click', function (e) {
            var el = e.target.closest('[data-pki-copy],[data-pki-download],[data-pki-reveal],[data-goods-toggle]');
            if (!el) return;

            if (el.hasAttribute('data-goods-toggle')) {
                var box = document.getElementById(el.getAttribute('data-goods-toggle'));
                if (!box) return;
                var open = !box.hidden;
                box.hidden = open;
                el.setAttribute('aria-expanded', String(!open));
                el.textContent = (open ? '▸ ' : '▾ ') + el.textContent.slice(2);
                return;
            }

            if (el.hasAttribute('data-pki-reveal')) {
                var wrap = el.closest('.pki-out');
                if (!wrap) return;
                var shown = wrap.classList.toggle('revealed');
                el.textContent = t(shown ? 'ui.hide' : 'ui.reveal');
                return;
            }

            var targetId = el.getAttribute('data-pki-copy') || el.getAttribute('data-pki-download');
            var node = document.getElementById(targetId);
            if (!node) return;
            var text = node.textContent;

            if (el.hasAttribute('data-pki-copy')) {
                copy(text).then(function () {
                    var prev = el.textContent;
                    el.textContent = t('ui.copied');
                    el.classList.add('is-on');
                    setTimeout(function () { el.textContent = prev; el.classList.remove('is-on'); }, 1400);
                }).catch(function (err) {
                    if (window.__handleError) window.__handleError(err, 'pki-copy');
                });
            } else {
                var name = el.getAttribute('data-filename') || 'download.txt';
                var blob = new Blob([text], { type: 'application/x-pem-file' });
                if (window.__downloadFile) window.__downloadFile(blob, name);
            }
        });
    }

    function copy(text) {
        if (navigator.clipboard && window.isSecureContext) return navigator.clipboard.writeText(text);
        // Fallback for insecure origins and older WebViews.
        return new Promise(function (resolve, reject) {
            var ta = document.createElement('textarea');
            ta.value = text;
            ta.setAttribute('readonly', '');
            ta.style.cssText = 'position:fixed;top:-1000px;opacity:0';
            document.body.appendChild(ta);
            ta.select();
            try { document.execCommand('copy') ? resolve() : reject(new Error('copy failed')); }
            catch (e) { reject(e); }
            finally { document.body.removeChild(ta); }
        });
    }

    /* ── input plumbing ────────────────────────────────────────── */

    /**
     * Wire a textarea that also accepts dropped and pasted files. Both paths
     * end in the same callback with text or bytes, so tools never branch on
     * how the data arrived.
     */
    function attachInput(el, onData) {
        var zone = el.closest('.pki-drop') || el;

        ['dragenter', 'dragover'].forEach(function (ev) {
            zone.addEventListener(ev, function (e) {
                e.preventDefault(); e.stopPropagation();
                zone.classList.add('dragover');
            });
        });
        ['dragleave', 'drop'].forEach(function (ev) {
            zone.addEventListener(ev, function (e) {
                e.preventDefault(); e.stopPropagation();
                if (ev === 'dragleave' && zone.contains(e.relatedTarget)) return;
                zone.classList.remove('dragover');
            });
        });
        zone.addEventListener('drop', function (e) {
            var files = e.dataTransfer && e.dataTransfer.files;
            if (files && files.length) readFiles(files, onData);
        });
        el.addEventListener('paste', function (e) {
            var items = e.clipboardData && e.clipboardData.files;
            if (items && items.length) { e.preventDefault(); readFiles(items, onData); }
        });
    }

    function readFiles(fileList, onData) {
        var files = Array.prototype.slice.call(fileList);
        var results = [];
        var pending = files.length;
        files.forEach(function (file, idx) {
            var reader = new FileReader();
            reader.onload = function () {
                results[idx] = { name: file.name, bytes: new Uint8Array(reader.result) };
                if (--pending === 0) onData(results);
            };
            reader.onerror = function () {
                if (window.__handleError) window.__handleError(reader.error, 'pki-read');
                if (--pending === 0) onData(results.filter(Boolean));
            };
            reader.readAsArrayBuffer(file);
        });
    }

    /**
     * Interpret whatever the user gave us and explain how it was read. The
     * `notes` from the sniffer become a visible line ("this looked like a
     * Kubernetes secret") so a surprising result is self-explaining.
     */
    function interpret(input) {
        var Pem = window.PKI.Pem;
        var res = Pem.sniff(input);
        res.explain = res.notes.map(function (n) { return t('ui.note_' + n.replace(/-/g, '_')); })
            .filter(Boolean);
        return res;
    }

    function message(el, kind, text) {
        if (!el) return;
        el.className = 'pki-msg ' + kind + (text ? ' visible' : '');
        el.textContent = text || '';
    }

    /** Map an engine error code to localised copy; unknown codes show raw. */
    function errorText(err) {
        var msg = String(err && err.message ? err.message : err);
        var key = msg.split(':')[0];
        var localised = t('errors.' + key.toLowerCase());
        if (localised) return localised;
        return t('errors.generic') ? substitute(t('errors.generic'), { detail: msg }) : msg;
    }

    /* ── daily cap ─────────────────────────────────────────────────────────
     *
     * These seven tools all bill the same shape of thing, so the counting rule
     * lives here once instead of seven times.
     *
     * The unit is the SUBJECT of the work — a certificate, a domain, a key, an
     * identity — never a press. Every one of these tools is used by iterating:
     * you decode a certificate, then type a hostname and decode it again; you
     * check a domain, fix the DNS record, and check it again; you convert one
     * key and try a second target format because the first was not what the
     * appliance wanted. Charging per press would make the cap bite hardest on
     * exactly the person the tool is for, and the honest number on the label
     * ("10 certificates") would be a lie about what 10 buys.
     *
     * So the caller passes what the run is ABOUT and gets a stable key for it;
     * repeating the same subject the same day is free (the unique-key rule in
     * tool-usage-limit.js). Cheap sync SHA-256 from our own ciphers module —
     * begin() must stay synchronous, so WebCrypto's async digest is unusable.
     *
     *     var run = U.beginRun('cert-decoder', der);   // Uint8Array or string
     *     if (!run) return;                            // wall already shown
     *     ... work ...
     *     run.commit();        // a result was DELIVERED
     *     run.abort();         // nothing was produced — never charge
     *
     * Fails OPEN, deliberately and in two directions: if tool-usage-limit.js
     * did not load, or if hashing the subject throws, the run proceeds. A
     * broken counter must never brick a working tool — same rule as cap().
     */
    // Four FNV-1a lanes, differing by seed and prime. Deliberately NOT
    // K.Ciphers.sha256, which was the first version of this and was wrong:
    // /acme-troubleshooter loads no engine module at all (it asks the server,
    // it never parses ASN.1), so window.PKI is undefined there, unitKey threw,
    // and begin() silently fell back to counting PRESSES. The tool's own copy
    // promises in nine locales that re-checking a domain does not count again —
    // the sentence would have been a lie on the one tool whose entire workflow
    // is re-checking. A key primitive may not depend on which modules a page
    // happens to load.
    //
    // Non-cryptographic on purpose: this is a dedup key in the visitor's own
    // localStorage, and all it has to do is tell one certificate from another
    // within one day. A collision fails open (a second subject goes free),
    // which is the same direction every other failure here takes.
    var FNV_SEED  = [0x811c9dc5, 0x01000193, 0x9e3779b9, 0x85ebca6b];
    var FNV_PRIME = [16777619, 2166136261, 40503, 2654435761];

    function unitKey(input) {
        try {
            var bytes;
            if (typeof input === 'string') {
                bytes = new TextEncoder().encode(input.replace(/\s+/g, ''));
            } else if (input && typeof input.length === 'number') {
                bytes = input;
            } else { return null; }

            var n = bytes.length | 0;
            if (!n) return null;

            var h = [FNV_SEED[0], FNV_SEED[1], FNV_SEED[2], FNV_SEED[3]];
            for (var i = 0; i < n; i++) {
                var b = bytes[i] & 0xff;
                for (var k = 0; k < 4; k++) {
                    h[k] = Math.imul((h[k] ^ b) >>> 0, FNV_PRIME[k]) >>> 0;
                }
            }

            var out = '';
            for (var j = 0; j < 4; j++) out += ('0000000' + h[j].toString(16)).slice(-8);
            return out + '.' + n.toString(36);      // length separates same-hash-different-size
        } catch (e) { return null; }
    }

    var FREE_RUN = { commit: function () {}, abort: function () {} };

    function beginRun(slug, subject) {
        if (!window.__toolLimit) return FREE_RUN;
        return window.__toolLimit.begin(slug, subject == null ? null : unitKey(subject));
    }

    window.PkiUI = {
        init: init, t: t, esc: esc, code: code, date: date, bytesLabel: bytesLabel,
        verdict: verdict, findings: findings, fields: fields, section: section,
        chips: chips, dn: dn, output: output, bind: bind, copy: copy,
        attachInput: attachInput, readFiles: readFiles, interpret: interpret,
        message: message, errorText: errorText, substitute: substitute,
        beginRun: beginRun, unitKey: unitKey
    };
})();
